// The OXIDE field GUIDEBOOK: a two-page book overlay opened by using the guidebook item (hotbar). It
// teaches controls + the core survival/build/raid rules. Pure DOM (matches the game's other overlays);
// pages are authored below as HTML fragments, shown two at a time as an open-book spread. Prev/Next +
// arrow keys turn the leaves; Esc or click-out closes. Skinned like every other window: scrim behind
// a glass panel, one mono family, hotkeys as key chips.
import { SK, kbd, panelCss, redHover } from './skin';

const INK = SK.textHi;   // headings + bold
const INK_SOFT = SK.dim; // asides, italics, the counter
const RULE = SK.border;  // page divider

// Each entry is ONE page. They are shown two per spread (left + right).
const PAGES: string[] = [
  // 0 -- cover
  `<div style="height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;text-align:center;gap:18px">
     <div style="font:800 46px ${SK.font};letter-spacing:${SK.lsDisplay};color:${INK}">OXIDE</div>
     <div style="width:70px;height:2px;background:${INK_SOFT};opacity:0.5"></div>
     <div style="font:italic ${SK.fsHead} ${SK.font};color:${INK_SOFT};max-width:80%">A castaway's field guide</div>
     <div style="position:absolute;bottom:26px;font:${SK.fsSmall} ${SK.font};color:${INK_SOFT};opacity:0.7">turn the page &rsaquo;</div>
   </div>`,
  // 1 -- welcome + controls
  `<h2>Welcome</h2>
   <p>This field guide teaches you to survive, and a few things you would not otherwise know. Read it whenever you are lost. You always carry it.</p>
   <h3>Controls</h3>
   <ul>
     <li>${kbd('Q')} &mdash; open the craft menu</li>
     <li>${kbd('E')} &mdash; interact (doors, chests, stations, loot)</li>
     <li>${kbd('V')} &mdash; toggle first / third person</li>
     <li><b>Third person:</b> right-click interacts <i>or</i> aims a weapon</li>
     <li><b>First person:</b> interaction is ${kbd('E')} <b>only</b> &mdash; not right-click</li>
   </ul>`,
  // 2 -- the goal
  `<h2>The Goal</h2>
   <p>Build. Survive. Raid the other castaways. You have a <b>two-week wipe cycle</b>.</p>
   <p>At the end of the wipe a <b>rescue ship</b> descends from the sky. If you are still alive and you reach it, everything <b>on your person</b> travels with you to the next wipe. Everything left in the world is lost to the tide.</p>
   <p style="color:${INK_SOFT};font-style:italic">So: get strong, stay hidden, and be standing when the ship comes.</p>`,
  // 3 -- first steps
  `<h2>First Steps</h2>
   <ol>
     <li>Punch trees and rocks with your <b>Rock</b> for wood and stone.</li>
     <li>Press ${kbd('Q')} and craft a <b>Hammer</b> (it builds and upgrades) and a <b>Blueprint</b> (it plans pieces).</li>
     <li>Craft foundations, walls and a doorway. Wall yourself in before dark.</li>
     <li>Light a <b>Torch</b> &mdash; nights are truly dark, and a torch is how you see.</li>
   </ol>
   <p style="color:${INK_SOFT};font-style:italic">Tip: your first base only needs to survive the first night. It can be ugly.</p>`,
  // 4 -- base building
  `<h2>Building &amp; Airlocks</h2>
   <p>Bases are <b>modular</b>: foundations carry walls, walls carry floors, floors carry the next storey. Pieces snap into sockets &mdash; aim and they click together.</p>
   <p>An <b>airlock</b> is two doorways with a tiny room between. You close the outer door before opening the inner one, so a raider chasing you cannot simply follow you in. Every good base has one.</p>
   <p>Upgrade tiers with the hammer: <b>Twig &rarr; Wood &rarr; Stone &rarr; Metal &rarr; Reinforced</b>. Higher tiers cost more but take far more to break.</p>`,
  // 5 -- doors + locks
  `<h2>Doors &amp; Locks</h2>
   <ul>
     <li><b>Doors &amp; double doors</b> fit into doorways and swing on ${kbd('E')}.</li>
     <li>A <b>door with no lock is open to anyone.</b> Always lock it.</li>
     <li>A <b>wooden lock</b> opens for you and your team.</li>
     <li>A <b>keypad lock</b> opens for anyone who knows the 4-digit code &mdash; set one, share it only with friends.</li>
   </ul>
   <p style="color:${INK_SOFT};font-style:italic">Wood doors can be chopped down with effort. Metal and reinforced doors need explosives.</p>`,
  // 6 -- base claims
  `<h2>Base Claims <span style="color:${SK.warn}">(important)</span></h2>
   <p>A <b>Base Claim</b> is the heart of your base. Place one and the ground around it becomes <b>yours</b>: only you and the teammates you authorize can build, remove, or place inside it.</p>
   <p>Keep the claim <b>stocked with resources</b> &mdash; it spends a little each day as upkeep. If it runs empty, your walls slowly <b>decay</b> and crumble. An unclaimed base rots on its own.</p>
   <p style="color:${INK_SOFT};font-style:italic">No claim, no base. Place it first, authorize your friends, keep it fed.</p>`,
  // 7 -- windows + line of sight
  `<h2>Windows &amp; Line of Sight</h2>
   <p><b>Window bars</b> fit into window openings: they let you see out and shoot out, but stop anyone climbing in. Bar every window you can reach.</p>
   <h3>Line of sight</h3>
   <p>Inside a <b>sealed</b> room, outsiders cannot see in &mdash; the world beyond your walls goes dark to them. You only see out through the doors and windows you are <b>actually facing</b>.</p>
   <p style="color:${INK_SOFT};font-style:italic">Hide behind a wall and you vanish. Use it &mdash; and remember they can too.</p>`,
  // 8 -- survival tips / back cover
  `<h2>Odds &amp; Ends</h2>
   <ul>
     <li>Raw meat can poison you. <b>Cook it</b> on a campfire first.</li>
     <li>Drink from lakes and rivers before your thirst bites.</li>
     <li>Twig and wood pieces fall to a few hits &mdash; don't sleep behind them.</li>
     <li>When you log off you lie down as a <b>sleeper</b>. You can be found, looted, and chopped &mdash; log off somewhere safe.</li>
     <li>Plant <b>seeds</b> in a planter for a steady food supply.</li>
   </ul>
   <p style="text-align:center;color:${INK_SOFT};font-style:italic;margin-top:14px">Good luck, castaway. Make it to the ship.</p>`,
];

export class Guidebook {
  private readonly backdrop: HTMLDivElement;
  private readonly leftPage: HTMLDivElement;
  private readonly rightPage: HTMLDivElement;
  private readonly counter: HTMLDivElement;
  private spread = 0; // which two-page spread (0 = pages 0,1)
  private open_ = false;
  onClose: (() => void) | null = null;

  get isOpen(): boolean { return this.open_; }

  constructor() {
    this.backdrop = document.createElement('div');
    this.backdrop.style.cssText = `position:fixed;inset:0;z-index:40;display:none;align-items:center;justify-content:center;background:${SK.scrim}`;
    this.backdrop.addEventListener('mouseup', (e) => { if (e.button === 0 && e.target === this.backdrop) this.close(); });
    this.backdrop.addEventListener('contextmenu', (e) => { e.preventDefault(); if (e.target === this.backdrop) this.close(); });

    const book = document.createElement('div');
    book.dataset.ui = '1';
    book.style.cssText = [
      'position:relative', 'display:flex', 'width:min(880px,94vw)', 'height:min(560px,86vh)',
      panelCss('glass'), 'box-shadow:0 30px 80px rgba(0,0,0,0.6)',
      'padding:24px', 'box-sizing:border-box', 'color:' + SK.text, 'overflow:hidden',
    ].join(';');

    const pageCss = [
      'flex:1', 'padding:6px 26px', 'overflow:hidden', `font:${SK.fsBody}/1.55 ${SK.font}`,
    ].join(';');
    this.leftPage = document.createElement('div'); this.leftPage.style.cssText = pageCss + ';border-right:1px solid ' + RULE;
    this.rightPage = document.createElement('div'); this.rightPage.style.cssText = pageCss;
    book.append(this.leftPage, this.rightPage);

    // Center spine shadow.
    const spine = document.createElement('div');
    spine.style.cssText = 'position:absolute;left:50%;top:16px;bottom:16px;width:26px;transform:translateX(-50%);background:radial-gradient(ellipse at center,rgba(0,0,0,0.35),transparent 70%);pointer-events:none';
    book.appendChild(spine);

    const arrow = (txt: string, side: 'left' | 'right', fn: () => void): HTMLDivElement => {
      const b = document.createElement('div');
      b.textContent = txt;
      b.style.cssText = `position:absolute;${side}:-6px;top:50%;transform:translateY(-50%);width:44px;height:44px;display:flex;align-items:center;justify-content:center;font:28px ${SK.font};color:${SK.text};cursor:pointer;user-select:none;border-radius:${SK.rPill};background:${SK.surface2};border:1px solid ${SK.borderStrong};box-shadow:0 4px 14px rgba(0,0,0,0.4)`;
      b.addEventListener('mouseup', (e) => { e.stopPropagation(); fn(); });
      redHover(b);
      return b;
    };
    book.appendChild(arrow('‹', 'left', () => this.turn(-1)));
    book.appendChild(arrow('›', 'right', () => this.turn(1)));

    this.counter = document.createElement('div');
    this.counter.style.cssText = `position:absolute;bottom:10px;left:0;right:0;text-align:center;font:${SK.fsSmall} ${SK.font};font-variant-numeric:tabular-nums;color:` + INK_SOFT;
    book.appendChild(this.counter);

    // One shared stylesheet for the authored page HTML (headings/lists), scoped to the book.
    const style = document.createElement('style');
    style.textContent = `.oxbook h2{font:700 ${SK.fsTitle} ${SK.font};letter-spacing:${SK.lsTitle};text-transform:uppercase;margin:0 0 10px;color:${INK}} .oxbook h3{font:700 ${SK.fsHead} ${SK.font};margin:14px 0 4px;color:${INK}} .oxbook p{margin:0 0 10px} .oxbook ul,.oxbook ol{margin:0 0 10px;padding-left:20px} .oxbook li{margin:0 0 6px} .oxbook b{color:${INK}}`;
    this.leftPage.className = 'oxbook'; this.rightPage.className = 'oxbook';
    book.appendChild(style);

    this.backdrop.appendChild(book);
    document.body.appendChild(this.backdrop);
    window.addEventListener('keydown', (e) => {
      if (!this.open_) return;
      if (e.key === 'Escape') { e.preventDefault(); this.close(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); this.turn(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); this.turn(-1); }
    });
  }

  private turn(dir: number): void {
    const spreads = Math.ceil(PAGES.length / 2);
    this.spread = Math.max(0, Math.min(spreads - 1, this.spread + dir));
    this.render();
  }

  private render(): void {
    const l = this.spread * 2, r = l + 1;
    this.leftPage.innerHTML = PAGES[l] ?? '';
    this.rightPage.innerHTML = PAGES[r] ?? '';
    const spreads = Math.ceil(PAGES.length / 2);
    this.counter.textContent = `${this.spread + 1} / ${spreads}`;
  }

  toggle(): void { this.open_ ? this.close() : this.open(); }

  open(): void {
    this.open_ = true;
    this.spread = 0;
    this.render();
    this.backdrop.style.display = 'flex';
  }

  close(): void {
    if (!this.open_) return;
    this.open_ = false;
    this.backdrop.style.display = 'none';
    this.onClose?.();
  }
}
